// New version src/Common/controls/textfield/textfield.component.ts CommonTextfieldComponent
// Old version src/CaseDotStar.ServicePackages.Frontend.Common/scripts/common/controls/common_controls_textfield_module/ctrl_textfield_component_service.jsx CtrlTextfieldComponent

import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	Injector,
	Input,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import {
	extend,
	forEach,
	isBoolean,
	isFunction,
	isNumber,
	isString,
} from 'lodash';

import {
	Subscription,
	timer,
} from 'rxjs';

import * as Inputmask from 'inputmask';
import { CommonControlComponent } from '../control/control.component';
import { commonUtilitiesCoreService } from '../../services/core.service';
import { COMMON_THROTTLE_MILLISECONDS } from '../control/throttler_milliseconds.constant';
import { THTMLInputType } from '../../@types/core';
import { COMMON_HOT_KEYS } from '../control/hot_keys.constant';
import { commonBrowserService } from '../../services/browser.service';
import { COMMON_AUTOCOMPLETE_PROPS } from '../control/autocomplete_props.constant';

export type TCommonTextfieldModel = string | number | null;

@Component({
	selector: 'common-textfield',
	templateUrl: './textfield.component.pug',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: [
		'../control/control.component.sass',
		'./textfield.component.sass',
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CommonTextfieldComponent),
			multi: true,
		},
	],
})
export class CommonTextfieldComponent extends CommonControlComponent {
	@Input() model: TCommonTextfieldModel;
	@Output() modelChange = new EventEmitter<TCommonTextfieldModel>();
	@Output() changeEvent = new EventEmitter<TCommonTextfieldModel>();

	@Input() isDisabled = false;
	@Input() isReadonly = false;
	@Input() inputMask = '';
	@Input() inputMaskParams: any = null; // interface
	@Input() placeholder = '';
	@Input() floatLabel = '';
	@Input() autocomplete: string = COMMON_AUTOCOMPLETE_PROPS.AUTOCOMPLETE_OFF;
	@Input() autocapitalize: string = COMMON_AUTOCOMPLETE_PROPS.AUTOCAPITALIZE_OFF;
	@Input() autocorrect: string = COMMON_AUTOCOMPLETE_PROPS.AUTOCORRECT_OFF;
	@Input() spellcheck = 'true';
	@Input() name = '';
	@Input() type: THTMLInputType = 'text';
	@Input() iconClasses = '';
	@Input() iconTitle = '';
	@Input() withRightIcon: boolean;
	@Input() hideCount = false;
	@Input() ctrlClasses = '';
	@Input() hasClearIcon = false;
	@Input() hasDebounce = false;
	@Input() isUrl = false;
	@Input() parseUrl: (value: string) => string;
	@Input() isLoading = false;
	@Input() hasLoaderDelay = false;
	@Input() isUseHint = false;
	@Input() onHint: (model: TCommonTextfieldModel) => string;
	@Input() maxLength: number;

	@Output() focusEvent = new EventEmitter<Event>();
	@Output() blurEvent = new EventEmitter<Event>();
	@Output() enterEvent = new EventEmitter<KeyboardEvent>();
	@Output() keyDownEvent = new EventEmitter<KeyboardEvent>();
	@Output() keyUpEvent = new EventEmitter<KeyboardEvent>();
	@Output() clickEvent = new EventEmitter<MouseEvent>();
	@Output() iconClickEvent = new EventEmitter<MouseEvent>();
	@ViewChild('input', {static: true}) inputRef: ElementRef;

	modelIsEmpty = false;
	value = '';
	inputValue = '';
	isHintOn = false;
	hintValue = '';
	charsLeft = 0;

	protected idKey = 'textfield_';
	protected isInputMaskInitialized = false;
	protected setModel$: Subscription | null = null;

	constructor (protected injector: Injector) {
		super(injector);
	}

	ngOnInit () {
		super.ngOnInit();

		this.value = this.formattedModel(this.model);
		this.setInputValue(this.value);
		this.modelIsEmpty = this.isEmpty(this.value);
		this.charsLeft = this.getCharsLeft(this.value);

		if (this.isNeedEnableHint(this.model)) {
			this.hintValue = this.onHint(this.model);
			this.isHintOn = true;
		}
	}

	ngOnChanges (changes: SimpleChanges) {
		super.ngOnChanges(changes);

		if (changes.model) {
			this.updateModel(changes.model.previousValue, changes.model.currentValue);
		}
	}

	writeValue (value: any): void {
		this.updateModel(this.model, value);
	}

	isWithRightIcon (): boolean {
		return this.withRightIcon || this.hasClearIcon || this.isUrl || this.isLoading;
	}

	isWithCount (): boolean {
		return !!this.maxLength && !this.hideCount && !this.isDisabled;
	}

	onFocusHandler (event: Event): void {
		if (this.isDisabled) {
			return;
		}

		this.initInputMask();
		super.onFocusHandler(event);
		this.focusEvent.emit(event);
		this.disableHint();
	}

	onBlurHandler (event: Event): void {
		// For input mask
		// this.onChangeInput(this.inputRef.nativeElement.value, event);

		if (this.isDisabled) {
			return;
		}

		let model = this.model;

		// this._setSubmitted();
		super.onBlurHandler(event);

		// Cancel debounce and set model
		this.cancelSetModel(() => {
			model = this.parseValue(this.value);
			this.setModel(model);
		});

		this.blurEvent.emit(event);

		this.timeout(() => {
			if (this.isNeedEnableHint(model)) {
				this.enableHint();
			}
		});
	}

	onKeyDownInput (event: KeyboardEvent): void {
		if (this.isDisabled) {
			return;
		}

		if (event.which === COMMON_HOT_KEYS.ENTER) {
			this.enterEvent.emit(event);
		}

		this.keyDownEvent.emit(event);
	}

	onKeyUpInput (event: KeyboardEvent): void {
		// For input mask
		this.onChangeInput(this.inputRef.nativeElement.value);

		if (!this.isDisabled) {
			this.keyUpEvent.emit(event);
		}
	}

	onMouseUpInput (event: MouseEvent): void {
		if (commonBrowserService.browser.ie && event.which === COMMON_HOT_KEYS.RIGHT_MOUSE) {
			this.focus();
		}
	}

	onClickHandler (event?: MouseEvent): void {
		if (!this.isDisabled) {
			this.clickEvent.emit(event);
		}
	}

	onClickIconHandler (event?: MouseEvent): void {
		if (!this.isDisabled) {
			this.iconClickEvent.emit(event);
		}
	}

	onChangeInput (value: string) {
		if (this.isDisabled) {
			return;
		}

		const oldValue = this.value;

		if (value === this.value) {
			return;
		}

		// Saves async access to variables of an event (react)
		// event.persist && event.persist();

		const parseValue = this.parseValue(value);

		this.setValue(value, parseValue);

		if (this.isInputMaskInitialized && this.inputMask) {
			const inputmask = this.inputRef.nativeElement.inputmask;
			const unmaskAsNumber = inputmask.option('unmaskAsNumber');

			// Save '-' when user writes the first character
			if (!(unmaskAsNumber && oldValue === '' && value === '-')) {
				this.debounceSetModel(parseValue);
			}
		} else {
			this.debounceSetModel(parseValue);
		}
	}

	onPaste (): void {
		setTimeout(() => {
			this.onChangeInput(this.getInputEl().value);
		});
	}

	getInputEl (): HTMLInputElement {
		return this.inputRef.nativeElement;
	}

	detectChanges () {
		this.changeDetector.detectChanges();
	}

	// input value -> model
	parseValue (inputValue: string): TCommonTextfieldModel {
		const maxLength = this.maxLength;
		let value: TCommonTextfieldModel = inputValue.trim();

		if (maxLength) {
			value = this.limitTo(value, maxLength);
		}

		if (this.isInputMaskInitialized && this.inputMask) {
			const input = this.inputRef.nativeElement;
			const inputmask = input.inputmask;
			const unmaskAsNumber = inputmask.option('unmaskAsNumber');
			const autoUnmask = inputmask.option('autoUnmask');
			const unMaskValue = inputmask.unmaskedvalue();
			const placeholder = inputmask.option('placeholder');

			// Filter the placeholder value for IE
			if (placeholder && unMaskValue === '' && value === placeholder) {
				return '';
			}

			if (autoUnmask || unmaskAsNumber) {
				value = unMaskValue;

				if (isString(value)) {
					value = value.trim();
				}

				// Because of check (unmaskedValue = opts.onUnMask(bufferValue, unmaskedValue, opts) || unmaskedValue)
				// for numerical masks method unmaskedvalue return 0 as string, it is necessary to put this crutch
				// https://github.com/RobinHerbots/jquery.inputmask/blob/3.x/js/inputmask.js#L1736
				if (unmaskAsNumber && value === '0') {
					value = 0;
				}

				// In IE return '0' with empty sting in input
				if (!input.value) {
					value = null;
				}
			}
		}

		return value;
	}

	parseUrlFunc (url: string): string {
		if (this.parseUrl) {
			url = this.parseUrl(url);
		}

		if (!url.match(/^[a-zA-Z+]+:/)) {
			url = 'http://' + url;
		}

		return url;
	}

	clearModel (event: MouseEvent): void {
		event.preventDefault();
		// Don't forget about debounce
		this.cancelSetModel();

		this.setValue('', '');
		this.setModel('');
	}

	onClickClearButton (event: MouseEvent): void {
		if (this.isDisabled) {
			return;
		}

		this.mouseDownPreventDefault(event, () => this.clearModel(event));
	}

	protected updateModel (oldModel: TCommonTextfieldModel, newModel: TCommonTextfieldModel) {
		const hasValueInModels = commonUtilitiesCoreService.hasValue(newModel) || commonUtilitiesCoreService.hasValue(oldModel);

		if (hasValueInModels && newModel !== oldModel) {
			// Filter the placeholder value for IE
			if (this.isInputMaskInitialized && this.inputMask) {
				const inputmask = this.inputRef.nativeElement.inputmask;
				const unMaskValue = inputmask.unmaskedvalue();

				if (unMaskValue !== newModel) {
					const newValue = this.formattedModel(newModel);
					const placeholder = inputmask.option('placeholder');

					if (!(placeholder && newValue === '' && unMaskValue === '' && this.value === placeholder)) {
						this.setValue(newValue, newModel);
					}
				}
			} else {
				const newValue = this.formattedModel(newModel);

				this.setValue(newValue, newModel);
			}

			if (this.isNeedEnableHint(newModel)) {
				this.enableHint();
			} else {
				this.disableHint();
			}
		}
	}

	protected focus () {
		this.inputRef.nativeElement.focus();
	}

	protected initInputMask () {
		if (!this.isInputMaskInitialized && this.inputMask) {
			const input = this.inputRef.nativeElement;

			Inputmask(this.inputMask, this.inputMaskParams).mask(input);

			this.willUnbind(() => {
				// When run "inputmask.remove()" "input" is not have "form" field,
				// so inputmask can't remove "submit" handler
				// https://jira.parcsis.org/browse/CASEM-26200
				const events = input.inputmask.events;
				const form = input.form;

				forEach(events, (eventHandlers, eventName) => {
					if (-1 < ['submit', 'reset'].indexOf(eventName) && input.form) {
						while (0 < eventHandlers.length) {
							const eventHandler = eventHandlers.pop();
							form.removeEventListener(eventName, eventHandler);
						}

						delete events[eventName];
					}
				});

				// timeout so that the events on submit do not break, then remove the mask
				// https://jira.parcsis.org/browse/CASEM-22727
				this.runOutsideAngular(() => {
					this.timeout(() => {
						if (input.inputmask) {
							input.inputmask.remove();
						}
					});
				});
			});

			// Input mask beak event change
			// in IE without incomplete the garbage gets to model

			// The 'blur' event is needed to update numbers in the model, if inputmask params has min, max values
			// https://jira.parcsis.org/browse/CASEM-23473
			// this.willUnbind(
			// 	this.onEvent($input, 'keyup keydown paste cut incomplete blur', (event) => this._onInput(event))
			// );
			this.isInputMaskInitialized = true;
		}
	}

	// Formats value of model till a line of an input
	protected formattedModel (model: TCommonTextfieldModel): string {
		const maxLength = this.maxLength;
		let result = '';

		if (isString(model)) {
			result = model;
		}

		if (isNumber(model) && isFinite(model)) {
			result = model.toString();
		}

		result = result.trim();

		if (maxLength && result.length > maxLength) {
			result = this.limitTo(result, maxLength);
		}

		// In IE in case of an initialization doesn't fulfill input a mask
		// Blank lines not to lay down, behaves inadequately
		if (result && this.inputMask) {
			result = Inputmask.format(result, extend({ alias: this.inputMask }, this.inputMaskParams));
		}
		return result;
	}

	protected limitTo (val: string, limit: number): string {
		return val.substr(0, limit);
	}

	// Quantity of the remained free characters
	protected getCharsLeft (value: string): number {
		const maxLength = this.maxLength;

		if (maxLength && !this.hideCount) {
			return maxLength - value.length;
		} else {
			return 0;
		}
	}

	protected isEmpty (value: string): boolean {
		return !commonUtilitiesCoreService.hasValue(value);
	}

	protected isNeedEnableHint (model: TCommonTextfieldModel): boolean {
		return this.isUseHint && !!model;
	}

	// The single method which shall change value in state
	protected setValue (value: string, model: TCommonTextfieldModel): void {
		if (this.isTextfieldModel(value) && this.isTextfieldModel(model)) {
			this.value = value;
			this.model = model;
			this.modelIsEmpty = this.isEmpty(value);
			this.charsLeft = this.getCharsLeft(value);

			this.setInputValue(value);
		}
	}

	// Fix for inputmask
	protected setInputValue (value: string): void {
		if (value !== this.inputRef.nativeElement.value) {
			this.inputValue = value;
			this.inputRef.nativeElement.value = value;
		}
	}

	protected setModel (model: TCommonTextfieldModel): void {
		if (this.isTextfieldModel(model)) {
			this.modelChange.emit(model);
			this.changeEvent.emit(model);
			this.emitOnChange(model);
		}
	}

	protected debounceSetModel (model: TCommonTextfieldModel): void {
		if (this.isTextfieldModel(model)) {
			this.model = model;
		}

		if (this.hasDebounce) {
			this.cancelSetModel();

			this.setModel$ = timer(COMMON_THROTTLE_MILLISECONDS)
				.subscribe(() => {
					this.setModel(model);
					this.setModel$ = null;
				});
		} else {
			this.setModel(model);
		}
	}

	protected cancelSetModel (callback: CallableFunction | null = null) {
		if (this.setModel$) {
			this.setModel$.unsubscribe();

			if (isFunction(callback)) {
				callback();
			}
		}
	}

	protected enableHint (): void {
		const hintValue = this.onHint(this.model);

		this.isHintOn = true;
		this.hintValue = hintValue;
	}

	protected disableHint (): void {
		if (this.isHintOn) {
			this.isHintOn = false;
			this.hintValue = '';
		}
	}

	protected mouseDownPreventDefault (
		event: MouseEvent,
		callback: CallableFunction = (): void => void (0),
	): void {
		if (event.which === COMMON_HOT_KEYS.LEFT_MOUSE && !this.isDisabled) {
			// Save focus in input
			event.preventDefault();
			callback();
		}
	}

	// для случая когда commonTextfield используется в другом компоненте (например автокомплит)
	// в этом случае в текстфилд может попасть ngModel не того типа, напрмер объект
	private isTextfieldModel (model: TCommonTextfieldModel): boolean {
		return isNumber(model) || isString(model) || isBoolean(model) || !model;
	}
}
